/** Draft roster needs / limits aligned with league settings (starters + bench). */

import { canAffordPlayer } from "./salaryUtils";

function num(v, defaultVal) {
  const n = Number(v);
  return Number.isFinite(n) ? n : defaultVal;
}

export function getLeagueRosterCounts(leagueSettings = {}) {
  const qb = num(leagueSettings.qbCount ?? leagueSettings.qb_count, 1);
  const rb = num(leagueSettings.rbCount ?? leagueSettings.rb_count, 2);
  const wr = num(leagueSettings.wrCount ?? leagueSettings.wr_count, 2);
  const te = num(leagueSettings.teCount ?? leagueSettings.te_count, 1);
  const flex =
    num(leagueSettings.flexCount ?? leagueSettings.flex_count, 1) +
    num(leagueSettings.flexSilverCount ?? leagueSettings.flex_silver_count, 0) +
    num(leagueSettings.flexGoldCount ?? leagueSettings.flex_gold_count, 0);
  const k = num(leagueSettings.kCount ?? leagueSettings.k_count, 0);
  const bench = num(leagueSettings.benchCount ?? leagueSettings.bench_count, 5);
  const starterSlots = qb + rb + wr + te + flex + k;
  const total = starterSlots + bench;
  return { qb, rb, wr, te, flex, k, bench, starterSlots, total };
}

/** Total draft rounds / roster slots per team. */
export function getTotalRosterSize(leagueSettings = {}) {
  return getLeagueRosterCounts(leagueSettings).total;
}

/** Max draftable players at each position (starters + bench depth). */
export function getMaxPositionCounts(leagueSettings = {}) {
  const c = getLeagueRosterCounts(leagueSettings);
  const bench = c.bench;
  const flex = c.flex;
  return {
    QB: c.qb + bench,
    RB: c.rb + flex + bench,
    WR: c.wr + flex + bench,
    TE: c.te + flex + bench,
    K: c.k > 0 ? c.k + bench : 0,
  };
}

function normalizePosition(position) {
  return position ? String(position).trim().toUpperCase() : "";
}

export function getFilledStarters(roster, leagueSettings = {}) {
  const c = getLeagueRosterCounts(leagueSettings);
  const filled = { QB: 0, RB: 0, WR: 0, TE: 0, FLEX: 0, K: 0 };
  for (const p of roster || []) {
    const pos = normalizePosition(p.position);
    if (filled.QB < c.qb && pos === "QB") {
      filled.QB++;
      continue;
    }
    if (filled.RB < c.rb && pos === "RB") {
      filled.RB++;
      continue;
    }
    if (filled.WR < c.wr && pos === "WR") {
      filled.WR++;
      continue;
    }
    if (filled.TE < c.te && pos === "TE") {
      filled.TE++;
      continue;
    }
    if (filled.FLEX < c.flex && ["RB", "WR", "TE"].includes(pos)) {
      filled.FLEX++;
      continue;
    }
    if (c.k > 0 && filled.K < c.k && pos === "K") {
      filled.K++;
      continue;
    }
  }
  return filled;
}

export function getPositionCounts(roster) {
  const counts = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0 };
  for (const p of roster || []) {
    const pos = normalizePosition(p.position);
    if (counts[pos] !== undefined) counts[pos]++;
  }
  return counts;
}

/** Rows for UI: label, filled, required, complete */
export function getRosterNeedRows(roster, leagueSettings = {}) {
  const c = getLeagueRosterCounts(leagueSettings);
  const filled = getFilledStarters(roster, leagueSettings);
  const rows = [
    { key: "QB", label: "QB", filled: filled.QB, required: c.qb },
    { key: "RB", label: "RB", filled: filled.RB, required: c.rb },
    { key: "WR", label: "WR", filled: filled.WR, required: c.wr },
    { key: "TE", label: "TE", filled: filled.TE, required: c.te },
  ];
  if (c.flex > 0) {
    rows.push({ key: "FLEX", label: "FLEX", filled: filled.FLEX, required: c.flex });
  }
  if (c.k > 0) {
    rows.push({ key: "K", label: "K", filled: filled.K, required: c.k });
  }
  const starterAssigned =
    filled.QB + filled.RB + filled.WR + filled.TE + filled.FLEX + filled.K;
  const benchFilled = Math.max(0, (roster?.length || 0) - starterAssigned);
  rows.push({
    key: "BENCH",
    label: "Bench",
    filled: benchFilled,
    required: c.bench,
  });
  rows.push({
    key: "TOTAL",
    label: "Roster",
    filled: roster?.length || 0,
    required: c.total,
  });
  return rows;
}

/** Manual picks: any position within per-position caps until roster is full (bench flexible). */
export function canDraftPlayer(roster, player, leagueSettings = {}) {
  const pos = normalizePosition(player?.position);
  if (!pos) return false;
  const c = getLeagueRosterCounts(leagueSettings);
  if ((roster?.length || 0) >= c.total) return false;
  if (pos === "K" && c.k === 0) return false;

  const posCounts = getPositionCounts(roster);
  const max = getMaxPositionCounts(leagueSettings);
  const cap = max[pos];
  if (!cap) return false;
  return posCounts[pos] < cap;
}

/** Best legal pick for timer/autopick (same priority as manual draft). */
export function getNextAutoDraftPick(
  team,
  availablePlayers,
  draftType = "SNAKE",
  salaryCap = 200,
  leagueSettings = {}
) {
  const roster = team?.roster || [];
  const c = getLeagueRosterCounts(leagueSettings);
  const filled = getFilledStarters(roster, leagueSettings);
  const max = getMaxPositionCounts(leagueSettings);

  const getBest = (filterFn) => {
    let filtered = availablePlayers.filter(
      (p) => filterFn(p) && canDraftPlayer(roster, p, leagueSettings)
    );
    if (draftType === "SALARY_CAP") {
      filtered = filtered.filter((player) => canAffordPlayer(team, player, salaryCap));
    }
    return filtered.sort(
      (a, b) => (b.avg_fantasy_points ?? 0) - (a.avg_fantasy_points ?? 0)
    )[0];
  };

  if (filled.QB < c.qb) return getBest((p) => normalizePosition(p.position) === "QB");
  if (filled.RB < c.rb) return getBest((p) => normalizePosition(p.position) === "RB");
  if (filled.WR < c.wr) return getBest((p) => normalizePosition(p.position) === "WR");
  if (filled.TE < c.te) return getBest((p) => normalizePosition(p.position) === "TE");
  if (filled.FLEX < c.flex) {
    const posCounts = getPositionCounts(roster);
    for (const preferred of ["RB", "WR", "TE"].sort(
      (a, b) => (posCounts[a] || 0) - (posCounts[b] || 0)
    )) {
      const pick = getBest((p) => normalizePosition(p.position) === preferred);
      if (pick) return pick;
    }
    return getBest((p) => ["RB", "WR", "TE"].includes(normalizePosition(p.position)));
  }
  if (c.k > 0 && filled.K < c.k) return getBest((p) => normalizePosition(p.position) === "K");

  const posCounts = getPositionCounts(roster);
  const benchCandidates = availablePlayers.filter((p) => {
    const pos = normalizePosition(p.position);
    if (!canDraftPlayer(roster, p, leagueSettings)) return false;
    if (draftType === "SALARY_CAP" && !canAffordPlayer(team, p, salaryCap)) return false;
    return posCounts[pos] < (max[pos] || 0);
  });
  if (benchCandidates.length === 0) return undefined;
  return benchCandidates.sort((a, b) => {
    const posA = normalizePosition(a.position);
    const posB = normalizePosition(b.position);
    const headA = (max[posA] || 0) - (posCounts[posA] || 0);
    const headB = (max[posB] || 0) - (posCounts[posB] || 0);
    if (headB !== headA) return headB - headA;
    return (b.avg_fantasy_points ?? 0) - (a.avg_fantasy_points ?? 0);
  })[0];
}
